import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@travellegacy/nexops-design-system';
import { ConfirmDialog } from '@travellegacy/nexops-design-system';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@travellegacy/nexops-design-system';
import { receiptsAPI } from '@/shared/lib/api';
import { formatCurrency, formatDate } from '@/shared/lib/utils';
import { toast } from '@/shared/lib/toast';
import { useAuth } from '@/shared/contexts/AuthContext';
import {
  Loader2,
  Plus,
  Download,
  Trash2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

interface Receipt {
  id: string;
  number: number;
  recipientName: string;
  recipientEmail: string;
  recipientDocument: string;
  issueDate: string;
  items: Array<{ date: string; description: string; value: number }>;
  total: number;
  paymentMethod: string;
  pdfUrl: string;
  createdAt: string;
}

interface ReceiptItem {
  date: string;
  description: string;
  value: number;
}

export default function ReceiptsPage() {
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [receiptToDelete, setReceiptToDelete] = useState<Receipt | null>(null);
  const pageSize = 20;

  // Formulário
  const [company, setCompany] = useState<'TRAVEL_LEGACY' | 'UGOO'>('TRAVEL_LEGACY');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientDocument, setRecipientDocument] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('PIX');
  const [creditCardInstallments, setCreditCardInstallments] = useState('1');
  const [items, setItems] = useState<ReceiptItem[]>([
    { date: '', description: '', value: 0 },
  ]);

  const queryClient = useQueryClient();

  // Verificar se é administrador
  const isAdmin = user?.role === 'ADMIN';

  // Cores e configurações por empresa
  const companyConfig = {
    TRAVEL_LEGACY: {
      primaryColor: '#A0572F',
      beigeColor: '#F3E7E0',
      companyName: 'Travel Legacy LTDA',
      razaoSocial: 'Travel Legacy LTDA.',
      cnpj: '37.647.701/0001-27',
      endereco: {
        cep: '80.035-010',
        logradouro: 'R. Bom Jesus, 212 - Juvevê',
        cidade: 'Curitiba - PR',
      },
    },
    UGOO: {
      primaryColor: '#182840',
      beigeColor: '#E8EDF3',
      companyName: 'uGoo Viagens LTDA',
      razaoSocial: 'uGoo Viagens LTDA.',
      cnpj: '67.829.810/0001-26',
      endereco: {
        cep: '12.243-250',
        logradouro: 'Av. Doutor Nelson D\'Avila, 389, 114B',
        cidade: 'São José dos Campos - SP',
      },
    },
  };

  const currentConfig = companyConfig[company];

  // Funções de formatação
  const formatCPFCNPJ = (value: string): string => {
    const numbers = value.replace(/\D/g, '');

    if (numbers.length <= 11) {
      // CPF: 000.000.000-00
      return numbers
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    } else {
      // CNPJ: 00.000.000/0000-00
      return numbers
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
    }
  };

  // Formata string de entrada do usuário (digita números, formata como moeda)
  const formatCurrencyInput = (value: string): string => {
    // Remove tudo exceto números
    const numbers = value.replace(/\D/g, '');
    if (!numbers) return '';

    // Converte para número dividindo por 100 (centavos)
    const amount = parseFloat(numbers) / 100;

    // Formata como moeda brasileira
    return amount.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Converte número armazenado para string formatada para exibição
  const numberToCurrency = (value: number): string => {
    if (!value) return '';
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Converte string formatada de volta para número
  const parseCurrencyInput = (value: string): number => {
    if (!value) return 0;
    // Remove pontos de milhar e substitui vírgula decimal por ponto
    const numbers = value.replace(/\./g, '').replace(',', '.');
    return parseFloat(numbers) || 0;
  };

  const getPaymentMethodDisplay = (): string => {
    if (paymentMethod === 'PIX') return 'PIX';
    const installments = parseInt(creditCardInstallments);
    if (installments === 1) return 'Cartão de Crédito à vista';
    return `Cartão de Crédito em ${installments}x`;
  };

  // Query para listar recibos
  const { data: receiptsData, isLoading } = useQuery({
    queryKey: ['receipts', currentPage],
    queryFn: async () => {
      const response = await receiptsAPI.list({
        page: currentPage,
        limit: pageSize,
      });
      return response.data;
    },
  });

  const receipts = (receiptsData?.data || []) as Receipt[];
  const pagination = receiptsData?.pagination || {
    page: 1,
    limit: pageSize,
    total: 0,
    totalPages: 0,
  };

  // Mutation para criar recibo
  const createMutation = useMutation({
    mutationFn: async (data: {
      company?: 'TRAVEL_LEGACY' | 'UGOO';
      recipientName: string;
      recipientEmail: string;
      recipientDocument: string;
      items: ReceiptItem[];
      paymentMethod: string;
    }) => {
      const response = await receiptsAPI.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receipts'] });
      setIsDialogOpen(false);
      resetForm();
      toast.success('Recibo criado com sucesso!');
    },
    onError: (error: unknown) => {
      const errorMessage = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message || 'Erro ao criar recibo';
      toast.error('Erro ao criar recibo', errorMessage);
    },
  });

  // Mutation para deletar recibo
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await receiptsAPI.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['receipts'] });
      setDeleteDialogOpen(false);
      setReceiptToDelete(null);
      toast.success('Recibo deletado com sucesso!');
    },
    onError: (error: unknown) => {
      const errorMessage = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message || 'Erro ao deletar recibo';
      toast.error('Erro ao deletar recibo', errorMessage);
    },
  });


  const resetForm = () => {
    setCompany('TRAVEL_LEGACY');
    setRecipientName('');
    setRecipientEmail('');
    setRecipientDocument('');
    setPaymentMethod('PIX');
    setCreditCardInstallments('1');
    setItems([{ date: '', description: '', value: 0 }]);
  };

  const addItem = () => {
    setItems([...items, { date: '', description: '', value: 0 }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index: number, field: keyof ReceiptItem, value: string | number) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => sum + Number(item.value || 0), 0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validações
    if (!recipientName || !recipientEmail || !recipientDocument || !paymentMethod) {
      toast.warning('Preencha todos os campos obrigatórios');
      return;
    }

    if (items.some((item) => !item.date || !item.description || !item.value)) {
      toast.warning('Preencha todos os itens do recibo');
      return;
    }

    // Formatar datas para DD/MM/YYYY antes de enviar
    const formattedItems = items.map((item) => ({
      ...item,
      date: new Date(item.date + 'T00:00:00').toLocaleDateString('pt-BR'),
    }));

    createMutation.mutate({
      company,
      recipientName,
      recipientEmail,
      recipientDocument,
      items: formattedItems,
      paymentMethod: getPaymentMethodDisplay(),
    });
  };

  const handleDeleteClick = (receipt: Receipt) => {
    setReceiptToDelete(receipt);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (receiptToDelete) {
      deleteMutation.mutate(receiptToDelete.id);
    }
  };

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Recibos</h1>
          <p className="text-muted-foreground">Gere e gerencie recibos da empresa</p>
        </div>

        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Novo Recibo
        </Button>
      </div>

      {/* Dialog Modal */}
      {isDialogOpen && (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Emitir Novo Recibo</DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-6 p-6">
              {/* Formulário - Coluna Esquerda */}
              <form onSubmit={handleSubmit} className="space-y-6">
              {/* Seleção de Empresa */}
              <div className="space-y-3">
                <Select
                  id="company"
                  label="Empresa *"
                  value={company}
                  onChange={(e) => setCompany(e.target.value as 'TRAVEL_LEGACY' | 'UGOO')}
                  options={[
                    { value: 'TRAVEL_LEGACY', label: 'Travel Legacy' },
                    { value: 'UGOO', label: 'Ugoo' },
                  ]}
                />
              </div>

              {/* Dados do destinatário */}
              <div className="space-y-3">
                <h3 className="font-semibold text-sm">Dados do Recibo</h3>

                <div>
                  <label htmlFor="recipientName" className="block text-sm font-medium mb-1">
                    Nome Completo *
                  </label>
                  <Input
                    id="recipientName"
                    value={recipientName}
                    onChange={(e) => setRecipientName(e.target.value)}
                    placeholder="João Silva"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="recipientEmail">Email *</label>
                    <Input
                      id="recipientEmail"
                      type="email"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      placeholder="joao@example.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" htmlFor="recipientDocument">CPF/CNPJ *</label>
                    <Input
                      id="recipientDocument"
                      value={recipientDocument}
                      onChange={(e) => {
                        const formatted = formatCPFCNPJ(e.target.value);
                        setRecipientDocument(formatted);
                      }}
                      placeholder="000.000.000-00 ou 00.000.000/0000-00"
                      maxLength={18}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Select
                      id="paymentMethod"
                      label="Forma de Pagamento *"
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      options={[
                        { value: 'PIX', label: 'PIX' },
                        { value: 'CREDIT_CARD', label: 'Cartão de Crédito' },
                      ]}
                    />
                  </div>

                  {paymentMethod === 'CREDIT_CARD' && (
                    <div>
                      <Select
                        id="installments"
                        label="Parcelas *"
                        value={creditCardInstallments}
                        onChange={(e) => setCreditCardInstallments(e.target.value)}
                        options={[
                          { value: '1', label: 'À vista' },
                          ...Array.from({ length: 11 }, (_, i) => ({
                            value: (i + 2).toString(),
                            label: `${i + 2}x`,
                          })),
                        ]}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Itens */}
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-sm">Itens do Recibo</h3>
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    <Plus className="mr-2 h-4 w-4" />
                    Adicionar Item
                  </Button>
                </div>

                {items.map((item, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-2">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium">Item {index + 1}</span>
                      {items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1" htmlFor={`item-date-${index}`}>Data *</label>
                        <Input
                          id={`item-date-${index}`}
                          type="date"
                          value={item.date}
                          onChange={(e) => updateItem(index, 'date', e.target.value)}
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1" htmlFor={`item-description-${index}`}>Descrição *</label>
                        <Input
                          id={`item-description-${index}`}
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          placeholder="Serviço prestado"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1" htmlFor={`item-value-${index}`}>Valor (R$) *</label>
                        <Input
                          id={`item-value-${index}`}
                          value={item.value > 0 ? numberToCurrency(item.value) : ''}
                          onChange={(e) => {
                            const formatted = formatCurrencyInput(e.target.value);
                            const value = parseCurrencyInput(formatted);
                            updateItem(index, 'value', value);
                          }}
                          placeholder="0,00"
                          required
                        />
                      </div>
                    </div>
                  </div>
                ))}

              </div>

                <div className="flex justify-between items-center pt-2 w-full">
                  <div className="text-left">
                    <p className="text-sm text-muted-foreground">Total</p>
                    <p className="text-2xl font-bold">{formatCurrency(calculateTotal())}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsDialogOpen(false)}
                      disabled={createMutation.isPending}
                    >
                      Cancelar
                    </Button>
                    <Button type="submit" disabled={createMutation.isPending}>
                      {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Emitir Recibo
                    </Button>
                  </div>
                </div>
              </form>

              {/* Preview - Coluna Direita */}
              <div className="border-l pl-6">
                <h3 className="font-semibold text-sm mb-3">Preview do Recibo</h3>
                <div className="relative bg-white border rounded-lg shadow-sm overflow-hidden" style={{ width: '100%', aspectRatio: '210/297' }}>
                  {/* Blob decorativo - topo direito com "Recibo" */}
                  <div className="relative h-[100px]">
                    <div
                      className="absolute top-0 right-0 w-[250px] h-[100px] rounded-bl-[50px]"
                      style={{ backgroundColor: currentConfig.primaryColor }}
                    ></div>
                    <div className="absolute top-0 right-0 w-[250px] h-[100px] flex items-center justify-center">
                      <h1 className="text-white text-2xl font-bold">Recibo</h1>
                    </div>
                    {/* Logo no topo esquerdo */}
                    <div className="absolute top-[30px] left-[40px] w-[120px] h-[40px]">
                      {company === 'UGOO' ? (
                        <img src="/uGoo-navy.svg" alt="uGoo" className="h-full w-full object-contain" />
                      ) : (
                        <img src="/TravelLegacy-Preto.svg" alt="Travel Legacy" className="h-full w-full object-contain" />
                      )}
                    </div>
                  </div>

                  <div className="px-10 pt-4">
                    {/* Duas colunas: Reserva para / Detalhes */}
                    <div className="grid grid-cols-[55%_45%] gap-6 mb-8">
                      {/* Coluna Esquerda - Destinatário */}
                      <div>
                        <h3 className="font-semibold text-[13px] text-[#2B2B2B] mb-2">Reserva para:</h3>
                        <p
                          className="font-semibold text-[14px] mb-2 break-words"
                          style={{ color: currentConfig.primaryColor }}
                        >
                          {recipientName || 'Nome do destinatário'}
                        </p>
                        <p className="text-[11px] text-[#6B6B6B]">{recipientEmail || 'email@exemplo.com'}</p>
                        <p className="text-[11px] text-[#6B6B6B] mt-1">{recipientDocument || 'CPF/CNPJ'}</p>
                      </div>

                      {/* Coluna Direita - Detalhes */}
                      <div>
                        <h3 className="font-semibold text-[13px] text-[#2B2B2B] mb-2">Detalhes:</h3>
                        <p className="text-[11px] text-[#6B6B6B]">Número: -----</p>
                        <p className="text-[11px] text-[#6B6B6B] mt-1">
                          Data: {new Date().toLocaleDateString('pt-BR')}
                        </p>
                        <p className="text-[11px] text-[#6B6B6B] mt-3">{currentConfig.razaoSocial}</p>
                        <p className="text-[11px] text-[#6B6B6B] mt-1">CNPJ: {currentConfig.cnpj}</p>
                        <p className="text-[11px] text-[#6B6B6B] mt-3">CEP {currentConfig.endereco.cep}</p>
                        <p className="text-[11px] text-[#6B6B6B] mt-1">{currentConfig.endereco.logradouro}</p>
                        <p className="text-[11px] text-[#6B6B6B] mt-1">{currentConfig.endereco.cidade}</p>
                      </div>
                    </div>

                    {/* Tabela de Itens */}
                    <div className="mb-4">
                      {/* Cabeçalho com cor da empresa */}
                      <div
                        className="rounded-md px-5 py-2.5 grid grid-cols-[90px_1fr_130px] gap-4"
                        style={{ backgroundColor: currentConfig.primaryColor }}
                      >
                        <span className="text-white text-[11px] font-semibold">DATA</span>
                        <span className="text-white text-[11px] font-semibold">DESCRIÇÃO</span>
                        <span className="text-white text-[11px] font-semibold text-right">VALOR</span>
                      </div>

                      {/* Linhas de Itens */}
                      <div className="mt-2">
                        {items.length > 0 && items.some(item => item.description || item.value) ? (
                          items.map((item, index) => (
                            <div
                              key={index}
                              className={`grid grid-cols-[90px_1fr_130px] gap-4 px-5 py-2 ${
                                index < items.length - 1 ? 'border-b border-[#E3D9D2]' : ''
                              }`}
                            >
                              <span className="text-[11px] text-[#2B2B2B]">
                                {item.date ? new Date(item.date + 'T00:00:00').toLocaleDateString('pt-BR') : 'DD/MM/AAAA'}
                              </span>
                              <span className="text-[11px] text-[#2B2B2B] break-words">
                                {item.description || 'Descrição do item'}
                              </span>
                              <span className="text-[11px] text-[#2B2B2B] text-right">
                                {item.value ? formatCurrency(item.value) : 'R$ 0,00'}
                              </span>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-400 text-[11px] italic px-5 py-4">Nenhum item adicionado</p>
                        )}
                      </div>
                    </div>

                    {/* Total - com fundo bege e bloco da cor da empresa */}
                    <div
                      className="relative rounded-md h-[38px] mb-4 mt-2"
                      style={{ backgroundColor: currentConfig.beigeColor }}
                    >
                      <div
                        className="absolute right-0 top-0 rounded-md w-[230px] h-[38px] px-5 flex items-center justify-between"
                        style={{ backgroundColor: currentConfig.primaryColor }}
                      >
                        <span className="text-white text-[13px] font-semibold">TOTAL</span>
                        <span className="text-white text-[13px] font-semibold">
                          {formatCurrency(calculateTotal())}
                        </span>
                      </div>
                    </div>

                    {/* Forma de Pagamento */}
                    <div className="mt-5">
                      <h3 className="font-semibold text-[13px] text-[#2B2B2B]">Forma de pagamento:</h3>
                      <p className="text-[11px] text-[#6B6B6B] mt-2">
                        {getPaymentMethodDisplay()}
                      </p>
                    </div>
                  </div>

                  {/* Blob decorativo - inferior esquerdo */}
                  <div
                    className="absolute bottom-0 left-0 w-[210px] h-[90px] rounded-tr-[60px]"
                    style={{ backgroundColor: currentConfig.primaryColor }}
                  ></div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Lista de recibos */}
      <Card>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : receipts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum recibo encontrado. Crie o primeiro recibo acima.
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Destinatário</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead>Data</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Pagamento</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {receipts.map((receipt) => (
                    <TableRow key={receipt.id}>
                      <TableCell className="font-mono">
                        {String(receipt.number).padStart(5, '0')}
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{receipt.recipientName}</div>
                          <div className="text-sm text-muted-foreground">{receipt.recipientEmail}</div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {receipt.recipientDocument}
                      </TableCell>
                      <TableCell>{formatDate(receipt.issueDate)}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCurrency(receipt.total)}
                      </TableCell>
                      <TableCell>{receipt.paymentMethod}</TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                const response = await receiptsAPI.getDownloadUrl(receipt.id);
                                const downloadUrl = response.data.data.downloadUrl;
                                window.open(downloadUrl, '_blank');
                              } catch (error) {
                                toast.error('Erro ao gerar URL de download');
                              }
                            }}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            PDF
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(receipt)}
                              disabled={deleteMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Paginação */}
              {pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-muted-foreground">
                    Página {pagination.page} de {pagination.totalPages} ({pagination.total} recibos)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
                      disabled={currentPage === pagination.totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Excluir Recibo"
        description={
          receiptToDelete ? (
            <div className="space-y-2">
              <p>Tem certeza que deseja deletar o recibo <strong>#{String(receiptToDelete.number).padStart(5, '0')}</strong>?</p>
              <p className="text-sm text-muted-foreground">
                Destinatário: {receiptToDelete.recipientName}
              </p>
              <p className="text-sm text-muted-foreground">
                Valor: {formatCurrency(receiptToDelete.total)}
              </p>
              <p className="text-sm text-destructive mt-2">
                Esta ação não pode ser desfeita.
              </p>
            </div>
          ) : (
            'Tem certeza que deseja deletar este recibo? Esta ação não pode ser desfeita.'
          )
        }
        confirmText="Excluir"
        cancelText="Cancelar"
        variant="destructive"
        icon="delete"
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </>
  );
}
